// RangeFilter — date-range preset selector.
// Compact DROPDOWN (was a wide segmented pill-row): a single trigger showing the
// current range; the presets + a "Custom" chip + Custom-range date inputs live in
// a popover. Keeps the toolbar tidy on every portal page (Overview / Insights /
// Daily Log / Performance) and stops it overflowing when the workspace name /
// platform pills on the left are long.
import { useState, useRef, useEffect } from "react";
import { C } from "../lib/theme";
import { openDatePicker } from "../lib/datePicker";
import { RANGES } from "../lib/ranges";

export function RangeFilter({range, setRange, customFrom, setCustomFrom, customTo, setCustomTo}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const triggerRef = useRef(null);
  const supportsCustom = !!setCustomFrom && !!setCustomTo;
  const isCustom = range === "CUSTOM";
  const current = RANGES.find(r => r.id === range);
  const label = isCustom ? "Custom" : (current?.l || RANGES[0].l);
  // Close + return focus to the trigger (keyboard users keep their place).
  const close = () => { setOpen(false); triggerRef.current?.focus(); };

  useEffect(() => {
    if (!open) return;
    const onDoc = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = e => { if (e.key === "Escape") { setOpen(false); triggerRef.current?.focus(); } };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [open]);

  const chipStyle = sel => ({padding:"6px 11px",border:"none",borderRadius:7,background:sel?C.blue:"#f1f5f9",color:sel?"#fff":C.sub,fontSize:11.5,fontWeight:sel?700:600,cursor:"pointer",fontFamily:"inherit",boxShadow:sel?C.sBlue:"none",transition:"all .12s"});

  return (
    <div ref={ref} style={{position:"relative",display:"inline-flex"}}>
      <button ref={triggerRef} type="button" aria-label="Date range" aria-expanded={open} onClick={() => setOpen(o => !o)}
        style={{display:"flex",alignItems:"center",gap:8,padding:"7px 12px",borderRadius:9,border:`1px solid ${C.border}`,background:"#fff",color:C.text,fontSize:12.5,fontWeight:600,cursor:"pointer",fontFamily:"inherit",boxShadow:C.s1,whiteSpace:"nowrap"}}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={C.muted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
        {label}
        <span style={{color:C.muted,fontSize:10,marginInlineStart:1}}>{open?"▴":"▾"}</span>
      </button>

      {open && (
        <div className="nv-range-pop" style={{position:"absolute",top:"calc(100% + 6px)",insetInlineEnd:0,zIndex:60,width:236,background:"#fff",border:`1px solid ${C.border}`,borderRadius:10,boxShadow:C.s1,padding:9}}>
          <div style={{display:"flex",flexWrap:"wrap",gap:5}}>
            {RANGES.map(r => (
              <button key={r.id} type="button" onClick={() => { setRange(r.id); close(); }} style={chipStyle(range === r.id)}>
                {r.l}
              </button>
            ))}
            {/* Custom chip — one click (re)activates the CUSTOM window; stays open so
                the dates below can be picked. Highlighted while CUSTOM is active. */}
            {supportsCustom && (
              <button type="button" onClick={() => setRange("CUSTOM")} style={chipStyle(isCustom)}>
                Custom
              </button>
            )}
          </div>

          {supportsCustom && (
            <div style={{borderTop:`1px solid ${C.border2}`,marginTop:9,paddingTop:9}}>
              <div style={{fontSize:9,fontWeight:700,color:C.muted,textTransform:"uppercase",letterSpacing:.7,marginBottom:7}}>Custom range</div>
              <div style={{display:"flex",gap:8,alignItems:"flex-end"}}>
                <div style={{flex:1}}>
                  <label style={{fontSize:9,fontWeight:700,color:C.muted,textTransform:"uppercase",letterSpacing:.6,display:"block",marginBottom:4}}>From</label>
                  <input type="date" value={customFrom||""} onClick={openDatePicker} onFocus={openDatePicker} onChange={e=>{const v=e.target.value; setCustomFrom(v); if(customTo && v>customTo) setCustomTo(v); setRange("CUSTOM");}} max={new Date().toISOString().split("T")[0]}
                    style={{width:"100%",padding:"6px 8px",borderRadius:6,border:`1px solid ${isCustom?C.blue:C.border}`,fontSize:12,fontFamily:"inherit",background:"#fff",fontWeight:500}}/>
                </div>
                <div style={{color:C.muted,fontSize:13,paddingBottom:8,fontWeight:600}}>→</div>
                <div style={{flex:1}}>
                  <label style={{fontSize:9,fontWeight:700,color:C.muted,textTransform:"uppercase",letterSpacing:.6,display:"block",marginBottom:4}}>To</label>
                  <input type="date" value={customTo||""} onClick={openDatePicker} onFocus={openDatePicker} onChange={e=>{setCustomTo(e.target.value); setRange("CUSTOM");}} min={customFrom} max={new Date().toISOString().split("T")[0]}
                    style={{width:"100%",padding:"6px 8px",borderRadius:6,border:`1px solid ${isCustom?C.blue:C.border}`,fontSize:12,fontFamily:"inherit",background:"#fff",fontWeight:500}}/>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
